import React, { useEffect, useRef, useState } from "react";

import {
  HEALTH_CONCERNS,
  MAX_HEALTH_CONDITIONS,
} from "~/shared/utils/constants";
import { tw } from "~/utils";
import { ChevronDownIcon } from "../icons/ChevronDownIcon";

const specialties = HEALTH_CONCERNS;

interface SpecialtyDropdownProps {
  selectedSpecialties: string[];
  onSpecialtyChange: (specialties: string[]) => void;
}

const SpecialtyDropdown: React.FC<SpecialtyDropdownProps> = ({
  selectedSpecialties = [],
  onSpecialtyChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleSpecialty = (specialty: string) => {
    if (selectedSpecialties.includes(specialty)) {
      onSpecialtyChange(
        selectedSpecialties.filter((item) => item !== specialty),
      );
    } else if (selectedSpecialties.length < MAX_HEALTH_CONDITIONS) {
      onSpecialtyChange([...selectedSpecialties, specialty]);
    }
  };

  const isAtLimit = selectedSpecialties.length >= MAX_HEALTH_CONDITIONS;

  const displayText =
    selectedSpecialties.length > 0
      ? selectedSpecialties.length === 1
        ? `${selectedSpecialties[0]} (1 of ${MAX_HEALTH_CONDITIONS})`
        : `${selectedSpecialties.length} of ${MAX_HEALTH_CONDITIONS} selected`
      : `Select specialties (up to ${MAX_HEALTH_CONDITIONS})`;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-3 text-left text-sm font-medium hover:bg-gray-50"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="truncate text-sm font-medium">{displayText}</span>
        <ChevronDownIcon
          className={tw(
            "ml-2 size-4 shrink-0 text-gray-900 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 z-20 mt-1.5 w-[calc(100vw-2rem)] max-w-[460px] rounded-lg border border-gray-300 bg-white p-4 shadow-lg">
          <h3 className="mb-1 text-base font-medium">Your preferences</h3>
          <p className="mb-2 text-xs text-gray-500">
            Select up to {MAX_HEALTH_CONDITIONS}
          </p>
          <div className="border-t border-gray-200 pt-2">
            <div className="grid max-h-60 grid-cols-1 gap-y-3 overflow-y-auto sm:grid-cols-2 sm:gap-x-4">
              {specialties.map((specialty) => {
                const isSelected = selectedSpecialties.includes(specialty);
                const isDisabled = !isSelected && isAtLimit;

                return (
                  <div key={specialty} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`specialty-${specialty}`}
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => toggleSpecialty(specialty)}
                      className={tw(
                        "size-4 rounded accent-slate-700",
                        isDisabled && "cursor-not-allowed opacity-50",
                      )}
                    />
                    <label
                      htmlFor={`specialty-${specialty}`}
                      className={tw(
                        "ml-2 text-sm font-medium text-gray-900",
                        isDisabled && "cursor-not-allowed opacity-50",
                      )}
                    >
                      {specialty}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialtyDropdown;
